import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Flex, Heading, Table, Thead, Tbody, Tr, Th, Td,
  Badge, IconButton, Modal, ModalOverlay, ModalContent, ModalHeader,
  ModalBody, ModalFooter, ModalCloseButton, FormControl, FormLabel,
  Input, useDisclosure, useToast, Spinner, HStack, VStack,
} from '@chakra-ui/react';
import { FiPlus, FiTrash2, FiRefreshCw } from 'react-icons/fi';

export default function Organizations() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', slug: '' });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const token = localStorage.getItem('meet_token');
  const headers = { 'Authorization': `Token ${token}`, 'Content-Type': 'application/json' };

  const fetchOrgs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/organizations/', { headers });
      if (res.ok) setOrgs(await res.json());
    } catch (e) {
      toast({ title: 'Failed to load organizations', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const createOrg = async () => {
    try {
      const res = await fetch('/api/v1/organizations/', {
        method: 'POST', headers, body: JSON.stringify(form),
      });
      if (res.ok) {
        toast({ title: 'Organization created', status: 'success', duration: 2000 });
        onClose();
        setForm({ name: '', slug: '' });
        fetchOrgs();
      } else {
        const data = await res.json();
        toast({ title: 'Failed', description: JSON.stringify(data), status: 'error', duration: 4000 });
      }
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const deleteOrg = async (id) => {
    if (!window.confirm('Delete this organization?')) return;
    try {
      const res = await fetch(`/api/v1/organizations/${id}/`, { method: 'DELETE', headers });
      if (res.status === 204) {
        toast({ title: 'Organization deleted', status: 'success', duration: 2000 });
        fetchOrgs();
      }
    } catch (e) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={6}>
        <Heading size="lg" color="gray.700">Organizations</Heading>
        <HStack>
          <IconButton icon={<FiRefreshCw />} onClick={fetchOrgs} variant="ghost" aria-label="Refresh" />
          <Button leftIcon={<FiPlus />} colorScheme="teal" onClick={onOpen}>New Organization</Button>
        </HStack>
      </Flex>

      {loading ? (
        <Flex justify="center" py={12}><Spinner size="lg" color="teal.500" /></Flex>
      ) : (
        <Box bg="white" borderRadius="xl" boxShadow="sm" overflow="hidden">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Name</Th>
                <Th>Slug</Th>
                <Th>Rooms</Th>
                <Th>Status</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {orgs.map((org) => (
                <Tr key={org.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="500">{org.name}</Td>
                  <Td fontFamily="mono" fontSize="xs" color="gray.500">{org.slug}</Td>
                  <Td>{org.room_count}</Td>
                  <Td><Badge colorScheme={org.is_active ? 'green' : 'red'}>{org.is_active ? 'Active' : 'Inactive'}</Badge></Td>
                  <Td>
                    <IconButton size="xs" icon={<FiTrash2 />} variant="ghost" colorScheme="red"
                      onClick={() => deleteOrg(org.id)} aria-label="Delete" />
                  </Td>
                </Tr>
              ))}
              {orgs.length === 0 && (
                <Tr><Td colSpan={5} textAlign="center" py={8} color="gray.400">No organizations yet</Td></Tr>
              )}
            </Tbody>
          </Table>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create Organization</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  focusBorderColor="teal.400" />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Slug</FormLabel>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="e.g. my-org" focusBorderColor="teal.400" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="teal" onClick={createOrg} isDisabled={!form.name || !form.slug}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
